import { Box, Button, TextField, Typography } from '@mui/material';
import type { Label } from '@vy/types';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useUpdateLabel } from '../../../../hooks/requests/use-labels';
import { runMutation } from '../../../../utils/vy-mutation';
import { LabelColorPicker } from './LabelColorPicker';
import { LABEL_COLOR_PRESETS } from './labels-consts';
import type { LabelColorPreset } from './labels-types';

type EditLabelFormProps = {
  label: Label;
  onCancel: () => void;
  onSuccess?: () => void;
};

function findColorPreset(color: string, backgroundColor: string): LabelColorPreset {
  const found = LABEL_COLOR_PRESETS.find(
    (preset) => preset.color === color && preset.backgroundColor === backgroundColor,
  );
  return found ?? { color, backgroundColor };
}

export const EditLabelForm: FC<EditLabelFormProps> = ({ label, onCancel, onSuccess }) => {
  const [name, setName] = useState(label.name);
  const [selectedColor, setSelectedColor] = useState<LabelColorPreset>(() =>
    findColorPreset(label.color, label.backgroundColor),
  );

  const updateLabelMutation = useUpdateLabel();

  const handleSubmit = useCallback(() => {
    if (!name.trim()) {
      return;
    }

    void runMutation(
      updateLabelMutation,
      {
        labelId: label.id,
        updateLabelRequest: {
          name: name.trim(),
          color: selectedColor.color,
          backgroundColor: selectedColor.backgroundColor,
        },
      },
      {
        pending: 'Updating label...',
        success: 'Label updated',
        error: 'Failed to update label',
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
      },
    );
  }, [name, selectedColor, label.id, updateLabelMutation, onSuccess]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && name.trim()) {
        handleSubmit();
      } else if (event.key === 'Escape') {
        onCancel();
      }
    },
    [handleSubmit, onCancel, name],
  );

  const isSubmitDisabled = !name.trim() || updateLabelMutation.isLoading;

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
          mb: 1,
          display: 'block',
          fontWeight: 300,
          fontSize: '12px',
          lineHeight: '16px',
        }}
      >
        Choose a color
      </Typography>
      <LabelColorPicker selectedColor={selectedColor} onColorSelect={setSelectedColor} />

      <TextField
        data-vy="labels-edit-name-input"
        value={name}
        onChange={(changeEvent) => setName(changeEvent.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Label name"
        size="small"
        fullWidth
        autoFocus
        sx={{
          mt: 2,
          '& .MuiInputBase-root': {
            height: '40px',
            borderRadius: '8px',
            bgcolor: 'common.white',
            border: '1px solid',
            borderColor: 'divider',
            fontSize: '14px',
            fontWeight: '300',
            pl: 0,
          },
        }}
      />

      <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
        <Button
          data-vy="labels-edit-cancel"
          variant="text"
          size="small"
          sx={{
            borderRadius: '4px',
            px: 1.5,
            py: 1,
            bgcolor: 'background.paper',
            height: '30px',
            lineHeight: '16px',
          }}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          data-vy="labels-edit-submit"
          variant="contained"
          size="small"
          sx={{ flex: 1, height: '30px', lineHeight: '16px', fontWeight: 300 }}
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
